import { Box, Button, Dialog } from "@mui/material";
import { useContext, useState } from "react";
import { Cell, CellType, MigrateCellToRequiredDocument } from "generated/graphql";
import { P } from "theme/typography";
import { LoadingButton } from "@mui/lab";
import { FormFieldCellEditor } from "components/FormField/FormFieldCellEditor";
import { Close } from "@mui/icons-material";
import RequestStatus from "redux/slices/RequestStatus";
import { CellValueUnion, gqlCellValueFromValue } from "Backend";
import { useSnackbar } from "notistack";
import { runGqlMutation } from "gqlClient";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import { useAppSelector } from "redux/hooks";
import { selectLensEditorLensId } from "redux/slices/lensEditor";

type Props = {
  onClose: () => void;
  cell: Cell;
  onSuccess: () => void;
};

const RequiredCellDialog = (props: Props) => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const { enqueueSnackbar } = useSnackbar();
  const [cellValue, setCellValue] = useState<CellValueUnion | undefined>();
  const lensId = useAppSelector(selectLensEditorLensId);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const startMigration = (value: CellValueUnion, cell: { type: CellType; id: number }) => {
    const mappedVal = gqlCellValueFromValue(cell.type, value);
    if (mappedVal) {
      setStatus(RequestStatus.PENDING);
      runGqlMutation(MigrateCellToRequiredDocument, {
        cellId: cell.id,
        backfillValue: mappedVal,
      })
        .then((resp) => {
          setStatus(RequestStatus.SUCCEEDED);
          const count = resp.lens.form.migrateCellToRequired;
          enqueueSnackbar(`Successfully migrated to required. ${count} responses updated`, {
            variant: "success",
          });
          props.onSuccess();
          enqueueLensUpdateTimerSnackbar(lensId);
        })
        .catch(() => {
          setStatus(RequestStatus.FAILED);
          enqueueSnackbar(`Operation failed`, {
            variant: "error",
          });
        });
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => {
        props.onClose();
      }}>
      <Box
        sx={{
          padding: 2,
          width: 500,
          "& > * + *": {
            marginTop: 1,
          },
        }}>
        <Box
          sx={{
            marginBottom: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}>
          <Box
            sx={{
              "& > * + *": {
                marginTop: 1,
              },
            }}>
            <P variant="h5">Migrate Cell to Required</P>
            <P>Pick a default value to apply to existing Cases.</P>
          </Box>
          <Box>
            <Button
              color="secondary"
              onClick={() => {
                if (status === RequestStatus.PENDING) {
                  return;
                }
                props.onClose();
              }}>
              <Close />
            </Button>
          </Box>
        </Box>
        <Box>
          <FormFieldCellEditor
            label="Default"
            cell={props.cell}
            value={cellValue}
            setValue={(v) => {
              setCellValue(v);
            }}
            disabled={status === RequestStatus.PENDING}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={status === RequestStatus.PENDING}
            disabled={status === RequestStatus.PENDING || cellValue === undefined}
            onClick={() => {
              if (cellValue !== undefined) {
                startMigration(cellValue, props.cell);
              }
            }}>
            Migrate
          </LoadingButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RequiredCellDialog;
